import React, { useEffect, useState, Fragment } from 'react';
import { Building2, Check, Shield } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
export function ApplicationForm({
  onComplete
}: {
  onComplete?: () => void;
}) {
  const [step, setStep] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [done, setDone] = useState(false);
  const steps = ['Company', 'Trade References', 'Signatures'];
  const stepTitles = ['Company & Credit Details', 'Trade References', 'Signatures'];
  useEffect(() => {
    if (showModal) {
      const modalTimer = setTimeout(() => {
        setShowModal(false);
        setDone(true);
        if (onComplete) {
          onComplete();
        } else {
          setStep(0);
        }
      }, 2500);
      return () => clearTimeout(modalTimer);
    }
    if (done) return;
    const delay = step <= 1 ? 2100 : 4800;
    const timer = setTimeout(() => {
      if (step === 2) {
        setShowModal(true);
      } else {
        setStep(prev => prev + 1);
      }
    }, delay);
    return () => clearTimeout(timer);
  }, [step, showModal]);
  return <div className="w-full h-full bg-[#fafafa] rounded-xl overflow-hidden flex flex-col text-sm border border-gray-200 shadow-sm font-sans relative" data-id="element-3">
      {/* Header */}
      <div className="bg-white flex flex-col items-center py-2 shrink-0" data-id="element-4">
        <div className="w-8 h-8 bg-[#1a1f36] rounded-lg flex items-center justify-center mb-1 shadow-sm" data-id="element-5">
          <Building2 className="text-white w-4.5 h-4.5" data-id="element-6" />
        </div>
        <h2 className="text-lg font-bold text-gray-900" data-id="element-7">Credit Application</h2>
      </div>

      {/* Stepper */}
      <div className="bg-white border-y border-gray-200 py-3 pb-6 shrink-0" data-id="element-8">
        <div className="flex items-center w-full max-w-sm mx-auto px-6" data-id="element-9">
          {steps.map((label, i) => <Fragment key={i} data-id="element-10">
              <div className="relative flex flex-col items-center" data-id="element-11">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center bg-white z-10 transition-all duration-300
                    ${i < step ? 'border-[#6C5CE7] bg-[#6C5CE7]' : i === step ? 'border-[#6C5CE7]' : 'border-gray-300'}`} data-id="element-12">
                  {i < step ? <Check className="w-3 h-3 text-white" data-id="element-13" /> : i === step ? <motion.div layoutId="activeStep" className="w-2.5 h-2.5 bg-[#6C5CE7] rounded-full" data-id="element-14" /> : null}
                </div>
                <span className={`absolute top-7 text-[10px] whitespace-nowrap font-medium transition-colors duration-300
                    ${i === step ? 'text-[#6C5CE7]' : i < step ? 'text-[#6C5CE7]/60' : 'text-gray-400'}`} data-id="element-15">
                  {label}
                </span>
              </div>
              {i < steps.length - 1 && <div className="flex-1 h-0.5 mx-3 bg-gray-200 rounded-full relative overflow-hidden" data-id="element-16">
                  <motion.div className="absolute left-0 top-0 bottom-0 bg-[#6C5CE7] rounded-full" initial={{
              width: '0%'
            }} animate={{
              width: i < step ? '100%' : '0%'
            }} transition={{
              duration: 0.4
            }} data-id="element-17" />
                </div>}
            </Fragment>)}
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 px-6 py-3 overflow-hidden flex flex-col" data-id="element-18">
        <AnimatePresence mode="wait" data-id="element-19">
          <motion.div key={step} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -8
        }} transition={{
          duration: 0.15
        }} className="flex-1 flex flex-col min-h-0 max-w-3xl mx-auto w-full" data-id="element-20">
            <h3 className="text-sm font-bold text-gray-900 mb-2" data-id="element-21">
              {stepTitles[step]}
            </h3>

            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 flex-1" data-id="element-22">
              {/* Step 0: Company + Credit Request + Personal Guarantee */}
              {step === 0 && <div className="space-y-2.5" data-id="element-23">
                  <div className="grid grid-cols-3 gap-2.5" data-id="element-24">
                    <div className="col-span-2" data-id="element-25">
                      <label className="block text-[10px] font-semibold text-gray-600 mb-0.5" data-id="element-26">
                        Company Name <span className="text-red-500" data-id="element-27">*</span>
                      </label>
                      <div className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded text-gray-900 text-xs flex items-center h-7" data-id="element-28">
                        Dunder Mifflin Paper Company
                      </div>
                    </div>
                    <div data-id="element-29">
                      <label className="block text-[10px] font-semibold text-gray-600 mb-0.5" data-id="element-30">
                        EIN <span className="text-red-500" data-id="element-31">*</span>
                      </label>
                      <div className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded text-gray-900 text-xs flex items-center h-7" data-id="element-32">
                        23-7654321
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-2.5" data-id="element-33">
                    <div data-id="element-34">
                      <label className="block text-[10px] font-semibold text-gray-600 mb-0.5" data-id="element-35">
                        Industry <span className="text-red-500" data-id="element-36">*</span>
                      </label>
                      <div className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded text-gray-900 text-xs flex items-center h-7" data-id="element-37">
                        Paper & Office Supplies
                      </div>
                    </div>
                    <div data-id="element-38">
                      <label className="block text-[10px] font-semibold text-gray-600 mb-0.5" data-id="element-39">
                        Annual Revenue <span className="text-red-500" data-id="element-40">*</span>
                      </label>
                      <div className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded text-gray-900 text-xs flex items-center h-7" data-id="element-41">
                        $1M - $5M
                      </div>
                    </div>
                    <div data-id="element-42">
                      <label className="block text-[10px] font-semibold text-gray-600 mb-0.5" data-id="element-43">
                        Address <span className="text-red-500" data-id="element-44">*</span>
                      </label>
                      <div className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded text-gray-900 text-xs flex items-center h-7 truncate" data-id="element-45">
                        1725 Slough Ave, Scranton
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-100 pt-2.5" data-id="element-46">
                    <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-47">
                      Credit Request
                    </p>
                    <div className="grid grid-cols-2 gap-2.5" data-id="element-48">
                      <div data-id="element-49">
                        <label className="block text-[10px] font-semibold text-gray-600 mb-0.5" data-id="element-50">
                          Requested Credit Limit{' '}
                          <span className="text-red-500" data-id="element-51">*</span>
                        </label>
                        <div className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded text-gray-900 text-xs flex items-center h-7" data-id="element-52">
                          $50,000
                        </div>
                      </div>
                      <div data-id="element-53">
                        <label className="block text-[10px] font-semibold text-gray-600 mb-0.5" data-id="element-54">
                          Expected Monthly Volume{' '}
                          <span className="text-red-500" data-id="element-55">*</span>
                        </label>
                        <div className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded text-gray-900 text-xs flex items-center h-7" data-id="element-56">
                          $15,000
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Personal Guarantee */}
                  <div className="border-t border-gray-100 pt-2.5" data-id="element-57">
                    <div className="flex items-start gap-2 p-2 bg-gray-50 rounded border border-gray-200" data-id="element-58">
                      <div className="w-4 h-4 rounded border-2 border-[#6C5CE7] bg-[#6C5CE7] flex items-center justify-center shrink-0 mt-0.5" data-id="element-59">
                        <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} data-id="element-60" />
                      </div>
                      <span className="text-[10px] text-gray-700 leading-relaxed" data-id="element-61">
                        I personally guarantee the credit obligations of Dunder
                        Mifflin Paper Company, Inc.
                      </span>
                    </div>
                  </div>
                </div>}

              {/* Step 1: Trade References (2 refs) */}
              {step === 1 && <div className="space-y-3" data-id="element-62">
                  {/* Reference 1 */}
                  <div data-id="element-63">
                    <div className="flex justify-between items-center mb-2 pb-1.5 border-b border-gray-100" data-id="element-64">
                      <h4 className="font-semibold text-gray-800 text-xs" data-id="element-65">
                        Trade Reference 1
                      </h4>
                      <span className="text-[10px] text-gray-400 font-medium" data-id="element-66">
                        1 of 3
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2.5" data-id="element-67">
                      <div data-id="element-68">
                        <label className="block text-[10px] font-semibold text-gray-600 mb-0.5" data-id="element-69">
                          Company Name <span className="text-red-500" data-id="element-70">*</span>
                        </label>
                        <div className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded text-gray-900 text-xs flex items-center h-7" data-id="element-71">
                          Keystone Paper Pulp
                        </div>
                      </div>
                      <div data-id="element-72">
                        <label className="block text-[10px] font-semibold text-gray-600 mb-0.5" data-id="element-73">
                          Contact Name <span className="text-red-500" data-id="element-74">*</span>
                        </label>
                        <div className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded text-gray-900 text-xs flex items-center h-7" data-id="element-75">
                          John Mitchell
                        </div>
                      </div>
                      <div data-id="element-76">
                        <label className="block text-[10px] font-semibold text-gray-600 mb-0.5" data-id="element-77">
                          Phone <span className="text-red-500" data-id="element-78">*</span>
                        </label>
                        <div className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded text-gray-900 text-xs flex items-center h-7" data-id="element-79">
                          (555) 123-4567
                        </div>
                      </div>
                      <div data-id="element-80">
                        <label className="block text-[10px] font-semibold text-gray-600 mb-0.5" data-id="element-81">
                          Email <span className="text-red-500" data-id="element-82">*</span>
                        </label>
                        <div className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded text-gray-900 text-xs flex items-center h-7" data-id="element-83">
                          john@keystonepaper.com
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Reference 2 */}
                  <div data-id="element-84">
                    <div className="flex justify-between items-center mb-2 pb-1.5 border-b border-gray-100" data-id="element-85">
                      <h4 className="font-semibold text-gray-800 text-xs" data-id="element-86">
                        Trade Reference 2
                      </h4>
                      <span className="text-[10px] text-gray-400 font-medium" data-id="element-87">
                        2 of 3
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-2.5" data-id="element-88">
                      <div data-id="element-89">
                        <label className="block text-[10px] font-semibold text-gray-600 mb-0.5" data-id="element-90">
                          Company Name <span className="text-red-500" data-id="element-91">*</span>
                        </label>
                        <div className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded text-gray-900 text-xs flex items-center h-7" data-id="element-92">
                          The Box Company
                        </div>
                      </div>
                      <div data-id="element-93">
                        <label className="block text-[10px] font-semibold text-gray-600 mb-0.5" data-id="element-94">
                          Contact Name <span className="text-red-500" data-id="element-95">*</span>
                        </label>
                        <div className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded text-gray-900 text-xs flex items-center h-7" data-id="element-96">
                          Tom Anderson
                        </div>
                      </div>
                      <div data-id="element-97">
                        <label className="block text-[10px] font-semibold text-gray-600 mb-0.5" data-id="element-98">
                          Phone <span className="text-red-500" data-id="element-99">*</span>
                        </label>
                        <div className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded text-gray-900 text-xs flex items-center h-7" data-id="element-100">
                          (800) 242-2148
                        </div>
                      </div>
                      <div data-id="element-101">
                        <label className="block text-[10px] font-semibold text-gray-600 mb-0.5" data-id="element-102">
                          Email <span className="text-red-500" data-id="element-103">*</span>
                        </label>
                        <div className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded text-gray-900 text-xs flex items-center h-7" data-id="element-104">
                          tom@theboxcompany.com
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}

              {/* Step 2: Signatures */}
              {step === 2 && <div className="space-y-3 flex flex-col justify-center h-full" data-id="element-105">
                  {/* Terms block - condensed */}
                  <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded text-[10px] text-gray-500 leading-snug" data-id="element-106">
                    <p className="font-semibold text-gray-700 text-xs mb-0.5" data-id="element-107">
                      Terms & Conditions
                    </p>
                    <p data-id="element-108">
                      By signing, the undersigned certifies all information is
                      true and accurate, authorizes Credit Pulse to obtain
                      credit reports and verify references, and agrees to pay
                      invoices within agreed terms.
                    </p>
                  </div>

                  {/* Checkbox */}
                  <div className="flex items-center gap-2" data-id="element-109">
                    <motion.div className="w-4 h-4 rounded border-2 border-[#6C5CE7] bg-[#6C5CE7] flex items-center justify-center shrink-0" initial={{
                  scale: 0.5,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} transition={{
                  delay: 0.3,
                  type: 'spring'
                }} data-id="element-110">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} data-id="element-111" />
                    </motion.div>
                    <span className="text-[10px] text-gray-600" data-id="element-112">
                      I have read and agree to the Terms & Conditions.
                    </span>
                  </div>

                  {/* Signature */}
                  <div data-id="element-113">
                    <label className="block text-[10px] font-semibold text-gray-600 mb-1" data-id="element-114">
                      Signature
                    </label>
                    <div className="w-full h-16 bg-white border border-gray-200 rounded flex items-center justify-center relative overflow-hidden" data-id="element-115">
                      <motion.svg viewBox="0 0 240 40" className="w-48 h-8 absolute" data-id="element-116">
                        <motion.path d="M 8 28 C 15 8, 25 8, 32 22 S 45 32, 55 18 C 62 8, 72 12, 78 22 S 92 28, 100 15 C 108 5, 118 15, 125 22 S 140 28, 150 18 C 158 10, 168 15, 175 22 S 190 28, 200 15 L 220 20" fill="transparent" stroke="#1a1f36" strokeWidth="2" strokeLinecap="round" initial={{
                      pathLength: 0
                    }} animate={{
                      pathLength: 1
                    }} transition={{
                      duration: 1.8,
                      ease: 'easeInOut'
                    }} data-id="element-117" />
                      </motion.svg>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3" data-id="element-118">
                    <div data-id="element-119">
                      <label className="block text-[10px] font-semibold text-gray-600 mb-0.5" data-id="element-120">
                        Printed Name
                      </label>
                      <div className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded text-gray-900 text-xs flex items-center h-7" data-id="element-121">
                        Michael Scott
                      </div>
                    </div>
                    <div data-id="element-122">
                      <label className="block text-[10px] font-semibold text-gray-600 mb-0.5" data-id="element-123">
                        Date
                      </label>
                      <div className="w-full px-3 py-1.5 bg-white border border-gray-200 rounded text-gray-900 text-xs flex items-center h-7" data-id="element-124">
                        Oct 24, 2023
                      </div>
                    </div>
                  </div>
                </div>}
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="bg-white px-6 py-3 border-t border-gray-200 flex justify-between items-center shrink-0" data-id="element-125">
        <button className="px-4 py-1.5 border border-gray-300 rounded text-gray-700 text-xs font-medium" disabled={step === 0} data-id="element-126">
          Previous
        </button>
        <button className={`px-6 py-1.5 rounded text-xs font-medium text-white transition-colors shadow-sm ${step === 2 ? 'bg-green-600' : 'bg-[#6C5CE7]'}`} data-id="element-127">
          {step === 2 ? 'Submit Application' : 'Next'}
        </button>
      </div>

      {/* Submitted Modal Overlay */}
      <AnimatePresence data-id="element-128">
        {showModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="absolute inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-[2px] rounded-xl" data-id="element-129">
            <motion.div initial={{
          opacity: 0,
          scale: 0.85,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.9,
          y: 10
        }} transition={{
          type: 'spring',
          stiffness: 300,
          damping: 25
        }} className="bg-white rounded-2xl shadow-2xl border border-gray-200 px-10 py-8 flex flex-col items-center max-w-xs mx-4" data-id="element-130">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 250,
            damping: 18,
            delay: 0.15
          }} className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-131">
                <Check className="w-7 h-7 text-green-600" data-id="element-132" />
              </motion.div>
              <h3 className="text-lg font-bold text-gray-900 mb-1.5" data-id="element-133">
                Submitted!
              </h3>
              <p className="text-gray-500 text-xs text-center mb-4" data-id="element-134">
                Your credit application has been submitted and is now under
                review.
              </p>
              <div className="px-3 py-1 bg-green-50 text-green-700 rounded-full font-medium text-[10px] border border-green-100" data-id="element-135">
                Confirmation #DM-2024-0847
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}